import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiShoppingBag, FiHeart } from 'react-icons/fi';
import { useStore } from '../store/useStore';
import { useAuthStore } from '../store/useAuthStore';
import { CURRENCIES } from '../constants';
import { useCurrency } from '../hooks/useCurrency';
import '../styles/ProductDetail.css';

const API_BASE_URL = process.env.REACT_APP_API_URL;

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const products = useStore((state) => state.products);
  const addToCart = useStore((state) => state.addToCart);
  const showCartNotification = useStore((state) => state.showCartNotification);
  const user = useAuthStore((state) => state.user);
  const token = useAuthStore((state) => state.token);
  const { convertPrice, currencySymbol, selectedCurrency } = useCurrency();

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedColor, setSelectedColor] = useState(null);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customOrderLoading, setCustomOrderLoading] = useState(false);
  const [selectedSize, setSelectedSize] = useState('');
  const [showCustomOrderModal, setShowCustomOrderModal] = useState(false);
  const [showMadeToOrderModal, setShowMadeToOrderModal] = useState(false);

  const product = products.find((p) => p.id === parseInt(id));

  // Set first color as default when product loads
  useEffect(() => {
    if (product?.colors && product.colors.length > 0) {
      setSelectedColor(product.colors[0]);
      setCurrentImageIndex(0);
    }
  }, [product]);

  const checkWishlistStatus = useCallback(async () => {
    if (!API_BASE_URL || !token) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/wishlists`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        console.warn(`Wishlist fetch returned ${response.status}: ${response.statusText}`);
        setIsWishlisted(false);
        return;
      }

      const wishlist = await response.json();
      const isInWishlist = Array.isArray(wishlist) && wishlist.some((item) => item.product_id === parseInt(id));
      setIsWishlisted(isInWishlist);
    } catch (error) {
      console.error('Error checking wishlist:', error);
      setIsWishlisted(false);
    }
  }, [token, id]);

  // Scroll to top when product ID changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Check if product is in wishlist
  useEffect(() => {
    if (user && token) {
      checkWishlistStatus();
    }
  }, [user, token, id, checkWishlistStatus]);

  const handleColorSelect = (color) => {
    setSelectedColor(color);
    // Always show the first image of the selected color (in filtered list)
    setCurrentImageIndex(0);
  };

  // Get filtered images based on selected color (always filter, never show all)
  const getFilteredImages = () => {
    if (!product?.images || !selectedColor) return [];
    return product.images.filter((img) => img.color_id === selectedColor.id);
  };

  const filteredImages = getFilteredImages();

  // Handle image index when filtering - if current index is out of bounds, reset to 0
  const safeCurrentImageIndex = Math.min(currentImageIndex, filteredImages.length - 1);

  const handleWishlistToggle = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      if (isWishlisted) {
        // Remove from wishlist
        const response = await fetch(`${API_BASE_URL}/api/wishlists/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (response.ok) {
          setIsWishlisted(false);
        }
      } else {
        // Add to wishlist
        const response = await fetch(`${API_BASE_URL}/api/wishlists`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ productId: parseInt(id) }),
        });
        if (response.ok) {
          setIsWishlisted(true);
        }
      }
    } catch (error) {
      console.error('Error updating wishlist:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!product) {
    return (
      <div className="product-detail-page">
        <div className="product-detail-container">
          <div className="product-not-found">
            <h2>Product Not Found</h2>
            <p>The product you're looking for doesn't exist.</p>
            <Link to="/products" className="btn-back">
              Back to Shop
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const getStockForColorAndSize = (colorId, size) => {
    if (!product.stockByColor || !colorId || !size) return 0;
    const colorStock = product.stockByColor[colorId];
    if (!colorStock) return 0;
    const stock = colorStock[size];
    // Return actual quantity, treating 0 as valid (not out of stock, just no stock)
    return typeof stock === 'number' ? stock : 0;
  };

  const getAvailableSizes = () => {
    if (!product.stockByColor) return [];

    // If color is selected, get sizes for that specific color
    if (selectedColor && product.stockByColor[selectedColor.id]) {
      return Object.keys(product.stockByColor[selectedColor.id])
        .sort((a, b) => parseFloat(a) - parseFloat(b));
    }

    // If no color selected, get all unique sizes from all colors
    const allSizes = new Set();
    Object.values(product.stockByColor).forEach(colorStock => {
      Object.keys(colorStock).forEach(size => allSizes.add(size));
    });
    return Array.from(allSizes).sort((a, b) => parseFloat(a) - parseFloat(b));
  };

  const handleAddToCart = () => {
    if (product.colors && product.colors.length > 0 && !selectedColor) {
      alert('Please select a color first');
      return;
    }
    if (!selectedSize) {
      alert('Please select a color first and then select a size');
      return;
    }

    // Check if out of stock
    const stockLevel = getStockForColorAndSize(selectedColor?.id, selectedSize);
    if (stockLevel === 0) {
      // Show made-to-order confirmation modal
      setShowMadeToOrderModal(true);
      return;
    }

    // In stock - add directly
    addToCartDirectly();
  };

  const addToCartDirectly = () => {
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      color_id: selectedColor?.id || null,
      color: selectedColor?.color_name || null,
      size: selectedSize,
      quantity: 1,
    });

    // Show cart notification
    showCartNotification({
      productName: product.name,
      productImage: product.image,
      size: selectedSize,
      color: selectedColor?.color_name || null,
      price: product.price,
    });
  };

  const handleCustomOrder = async (quantity) => {
    // Prevent double-click submissions
    if (customOrderLoading) {
      return;
    }

    if (!user) {
      navigate('/login');
      return;
    }

    if (product.colors && product.colors.length > 0 && !selectedColor) {
      alert('Please select a color');
      return;
    }

    if (!selectedSize) {
      alert('Please select a size');
      return;
    }

    setCustomOrderLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/custom-orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          productId: product.id,
          size: selectedSize,
          quantity: parseInt(quantity),
        }),
      });

      const data = await response.json();

      if (response.ok) {
        alert('Custom order submitted! We will contact you soon to confirm.');
        setShowCustomOrderModal(false);
        setSelectedSize('');
        setSelectedColor(null);
      } else {
        alert(`Failed to create custom order: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating custom order:', error);
      alert('Error submitting custom order');
    } finally {
      setCustomOrderLoading(false);
    }
  };

  if (!product) {
    return (
      <div className="product-detail-page">
        <div className="product-detail-container">
          <button className="btn-back-simple" onClick={() => navigate(-1)}>
            ← Back
          </button>
          <div style={{ padding: '2rem', textAlign: 'center', color: '#999' }}>
            <p>Product not found. Please try again.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="product-detail-page">
      <div className="product-detail-container">
        <button className="btn-back-simple" onClick={() => navigate(-1)}>
          ← Back
        </button>

        <div className="product-detail-content">
          {/* Product Image Section with Thumbnails */}
          <div className="product-image-section">
            {/* Main Image Carousel */}
            <div className="product-detail-image">
              <div className="image-carousel">
                {filteredImages && filteredImages.length > 0 ? (
                  <img
                    src={filteredImages[safeCurrentImageIndex].image_url}
                    alt={`${product.name}-${safeCurrentImageIndex}`}
                    className="carousel-image"
                  />
                ) : (
                  <img src={product.image} alt={product.name} className="carousel-image" />
                )}
              </div>
            </div>

            {/* Thumbnail Gallery */}
            {filteredImages && filteredImages.length > 1 && (
              <div className="thumbnail-gallery">
                <div className="thumbnails-scroll">
                  {filteredImages.map((image, idx) => (
                    <button
                      key={idx}
                      className={`thumbnail ${idx === safeCurrentImageIndex ? 'active' : ''}`}
                      onClick={() => setCurrentImageIndex(idx)}
                      title={`View image ${idx + 1}`}
                    >
                      <img
                        src={image.image_url || image.image}
                        alt={`Thumbnail ${idx + 1}`}
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="product-detail-info">
            <div className="product-detail-header">
              {product.sku && (
                <p className="product-detail-sku">{product.sku}</p>
              )}
              <h1 className="product-detail-name">{product.name}</h1>
              <span className="product-detail-category">{product.category}</span>
            </div>

            <div className="product-detail-price">
              {product.discount_price && parseFloat(product.discount_price) > 0 ? (
                <>
                  <span className="original-price">
                    {currencySymbol}{convertPrice(product.price)}
                  </span>
                  <span className="discounted-price">
                    {currencySymbol}{convertPrice(product.discount_price)}
                  </span>
                </>
              ) : (
                `${CURRENCIES[selectedCurrency].symbol}${convertPrice(product.price)}`
              )}
            </div>

            {product.description && (
              <div className="product-detail-description">
                <h3>Description</h3>
                <div dangerouslySetInnerHTML={{ __html: product.description }} />
              </div>
            )}

            {/* Color Selector */}
            {product.colors && product.colors.length > 0 && (
              <div className="detail-color-selector">
                <label>
                  Color:
                  {selectedColor && (
                    <span className="color-name-label">{selectedColor.color_name}</span>
                  )}
                </label>
                <div className="color-options">
                  {product.colors.map((color) => (
                    <button
                      key={color.id}
                      className={`color-option ${selectedColor?.id === color.id ? 'selected' : ''}`}
                      onClick={() => handleColorSelect(color)}
                      title={color.color_name}
                      style={{
                        backgroundColor: color.color_hex,
                        borderColor: selectedColor?.id === color.id ? '#000' : '#ccc',
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Size Selector */}
            <div className="detail-size-selector">
              <label>Size (EU):</label>
              <div className="size-options-grid">
                {getAvailableSizes().map((size) => {
                  const isOutOfStock = getStockForColorAndSize(selectedColor?.id, size) <= 0;
                  return (
                    <button
                      key={size}
                      className={`size-option ${selectedSize === size ? 'selected' : ''} ${
                        isOutOfStock ? 'out-of-stock' : ''
                      }`}
                      onClick={() => setSelectedSize(size)}
                      title={`Size EU ${size}${isOutOfStock ? ' (Made to order)' : ''}`}
                    >
                      {size}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Quantity and Actions */}
            <div className="product-detail-actions">
              {selectedSize && (product.colors && product.colors.length > 0 ? selectedColor : true) ? (
                <button
                  className="btn-add-to-cart-large"
                  onClick={handleAddToCart}
                >
                  <FiShoppingBag /> Add to Cart
                </button>
              ) : (
                <button className="btn-add-to-cart-large" disabled style={{ cursor: 'not-allowed' }}>
                  <FiShoppingBag /> {!selectedSize ? 'Select Size' : 'Select Color'}
                </button>
              )}
              <button
                className={`btn-wishlist-large ${isWishlisted ? 'active' : ''}`}
                onClick={handleWishlistToggle}
                disabled={loading}
                title={isWishlisted ? 'Remove from Wishlist' : 'Add to Wishlist'}
              >
                <FiHeart />
              </button>
            </div>

            {/* Custom Order Modal */}
            {showCustomOrderModal && (
              <div className="custom-order-modal-overlay" onClick={() => setShowCustomOrderModal(false)}>
                <div className="custom-order-modal" onClick={(e) => e.stopPropagation()}>
                  <h3>Request Custom Order</h3>
                  <p>{product.name} - Size EU {selectedSize}</p>
                  <p className="custom-order-price">
                    Price: {product.discount_price && parseFloat(product.discount_price) > 0 ? (
                      <>
                        <span className="original-price">{currencySymbol}{convertPrice(product.price)}</span>
                        <span>{currencySymbol}{convertPrice(product.discount_price)}</span>
                      </>
                    ) : (
                      `${CURRENCIES[selectedCurrency].symbol}${convertPrice(product.price)}`
                    )}
                  </p>
                  <div className="custom-order-form">
                    <label>Quantity:</label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      defaultValue="1"
                      id="detail-custom-qty"
                    />
                  </div>
                  <p className="custom-order-note">
                    We'll contact you within 24 hours to confirm the order, discuss delivery timeline, and finalize details.
                  </p>
                  <div className="custom-order-actions">
                    <button
                      className="btn-submit-order"
                      onClick={() => handleCustomOrder(document.getElementById('detail-custom-qty').value)}
                      disabled={customOrderLoading}
                      style={{ opacity: customOrderLoading ? 0.6 : 1, cursor: customOrderLoading ? 'not-allowed' : 'pointer' }}
                    >
                      {customOrderLoading ? 'Submitting...' : 'Submit Order Request'}
                    </button>
                    <button
                      className="btn-cancel-order"
                      onClick={() => setShowCustomOrderModal(false)}
                      disabled={customOrderLoading}
                      style={{ opacity: customOrderLoading ? 0.6 : 1, cursor: customOrderLoading ? 'not-allowed' : 'pointer' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Made to Order Confirmation Modal */}
            {showMadeToOrderModal && (
              <div className="custom-order-modal-overlay" onClick={() => setShowMadeToOrderModal(false)}>
                <div className="custom-order-modal" onClick={(e) => e.stopPropagation()}>
                  <h3>⚠️ Made to Order Item</h3>
                  <p className="made-to-order-modal-text">
                    This item is currently out of stock and will be made to order.
                    Delivery may require additional time beyond our standard shipping timeframe.
                  </p>
                  <p className="made-to-order-modal-product">
                    {product.name} - {selectedColor?.color_name} - Size EU {selectedSize}
                  </p>
                  <div className="custom-order-actions">
                    <button
                      className="btn-submit-order"
                      onClick={() => {
                        setShowMadeToOrderModal(false);
                        addToCartDirectly();
                      }}
                    >
                      Add to Cart Anyway
                    </button>
                    <button
                      className="btn-cancel-order"
                      onClick={() => setShowMadeToOrderModal(false)}
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
